import {
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip
} from 'chart.js';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Crosshair,
  Factory,
  FileText,
  GitBranch,
  HelpCircle,
  History,
  Lightbulb,
  Maximize2,
  Minimize2,
  Moon,
  Network,
  PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen,
  School,
  Shield,
  Shrink,
  Sun,
  Target,
  TrendingDown, TrendingUp,
  Volume2,
  VolumeX,
  X,
  Zap
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Radar } from 'react-chartjs-2';
import { createPortal } from 'react-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import SignalVisualizer from '../../components/SignalVisualizer';
import api from '../../config/axiosConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { usePesa } from '../../stores/usePesaEngine';
import { K2000VoiceScanner } from './K2000VoiceScanner';
import RapportEnquete from './RapportEnquete';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const FAMILY_COLORS = {
  École: '#3b82f6', Travail: '#ef4444', Justice: '#6366f1',
  Art: '#f59e0b', Médiation: '#10b981', Famille: '#8b5cf6', Santé: '#ec4899'
};

// Ambiance → palette canvas
const AMBIANCE_PALETTE = {
  tension:    { bg: 'rgba(239, 68, 68, 0.47)',  link: 'rgba(239,68,68,0.08)',   particle: '#ef4444', glow: '#7f1d1d' },
  hope:       { bg: 'rgba(16, 185, 129, 0.41)', link: 'rgba(16,185,129,0.08)',  particle: '#10b981', glow: '#064e3b' },
  crisis:     { bg: 'rgba(245, 159, 11, 0.32)', link: 'rgba(245,158,11,0.10)', particle: '#f59e0b', glow: '#78350f' },
  resolution: { bg: 'rgba(99, 101, 241, 0.4)', link: 'rgba(99,102,241,0.06)', particle: '#818cf8', glow: '#1e1b4b' },
  default:    { bg: 'rgba(169, 85, 247, 0.53)', link: 'rgba(255,255,255,0.03)', particle: '#a855f7', glow: '#2e1065' },
};

// Archetype → comportement orbital
const ARCHETYPE_ORBIT = {
  opprimé:      { speed: 0.8,  radius: 1.0, pulse: true,  color: '#ef4444' },
  reproducteur: { speed: 0.3,  radius: 1.2, pulse: false, color: '#6366f1' },
  médiateur:    { speed: 0.6,  radius: 1.1, pulse: true,  color: '#10b981' },
  subversif:    { speed: 1.4,  radius: 0.9, pulse: true,  color: '#f59e0b' },
  institution:  { speed: 0.15, radius: 1.4, pulse: false, color: '#94a3b8' },
};

// Tension level → visual intensity
const TENSION_CONFIG = {
  1: { chaos: 0,    nodeJitter: 0,    linkOpacity: 0.04 },
  2: { chaos: 0.02, nodeJitter: 0.5,  linkOpacity: 0.06 },
  3: { chaos: 0.05, nodeJitter: 1.2,  linkOpacity: 0.08 },
  4: { chaos: 0.10, nodeJitter: 2.5,  linkOpacity: 0.12 },
  5: { chaos: 0.18, nodeJitter: 4.0,  linkOpacity: 0.18 },
};

const LEVEL_COLORS_CAUSAL = {
  débutant:      'border-blue-500/25   bg-blue-950/15   text-blue-300',
  intermédiaire: 'border-emerald-500/25 bg-emerald-950/15 text-emerald-300',
  avancé:        'border-amber-500/25  bg-amber-950/15  text-amber-300',
  expert:        'border-violet-500/25 bg-violet-950/15 text-violet-300',
};

const MONO_LABEL = `text-[9px] font-mono tracking-[0.22em] uppercase`;


const StatusTag = ({ children, color = 'purple' }) => {
  const map = {
    purple:  'bg-violet-500/10 border-violet-500/20 text-violet-400',
    amber:   'bg-amber-500/10  border-amber-500/20  text-amber-400',
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    red:     'bg-red-500/10    border-red-500/20    text-red-400',
    slate:   'bg-white/5       border-white/10      text-white/40',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[9px] font-mono tracking-widest ${map[color]}`}>
      {children}
    </span>
  );
};

const LabGrid = ({ darkMode }) => (
  <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.01]">
    <div 
      className="absolute inset-0" 
      style={{ 
        backgroundImage: `
          linear-gradient(${darkMode ? '#ffffff' : '#000000'} 1px, transparent 1px),
          linear-gradient(90deg, ${darkMode ? '#ffffff' : '#000000'} 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px'
      }}
    />
  </div>
);

const Scanlines = () => (
  <div className="fixed inset-0 pointer-events-none z-[1] opacity-[0.01]">
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-transparent h-2 animate-[scanline_8s_ease-in-out_infinite]" />
  </div>
);

const SoberWrapper = ({ children, className = '' }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: -8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8, scale: 0.98 }}
    transition={{ duration: 0.22, ease: 'easeOut' }}
    className={`rounded-xl overflow-hidden border border-white/[0.06] bg-black/20 backdrop-blur-md ${className}`}
  >
    {children}
  </motion.div>
);


export const DynamicsPanel = () => {
  const { engine } = usePesa();
  const dynamics    = engine?.case?.simulationEngine?.dynamics || [];
  const currentTick = engine?.currentTick || 0;
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setProgress(p => (p + 8) % 100), 600);
    return () => clearInterval(id);
  }, []);

  if (dynamics.length === 0) return null;

  return (
    <div className="p-4 space-y-3">
      {/* Cycle */}
      <div
        className="flex items-center justify-between px-3 py-2.5 rounded-lg"
        style={{ background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.1)' }}
      >
        <div>
          <div className={`${MONO_LABEL} text-white/25 mb-1`}>Cycle</div>
          <div className="h-1 w-32 rounded-full overflow-hidden bg-black/40">
            <motion.div
              className="h-full rounded-full"
              style={{ background: 'linear-gradient(90deg, #7c3aed, #f59e0b)', width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
        <span className="text-sm font-mono font-bold text-amber-400 tabular-nums">
          T-{String(currentTick).padStart(3, '0')}
        </span>
      </div>

      {/* Types */}
      {dynamics.slice(0, 4).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {dynamics.slice(0, 4).map((d, i) => (
            <StatusTag key={i} color="purple">{d.type || 'Process'}</StatusTag>
          ))}
        </div>
      )}
    </div>
  );
};

export const PredictionPanel = () => {
  const { engine, phase, currentSolution } = usePesa();
  const [showBayesNotice, setShowBayesNotice] = useState(false);

  const getStatus = (pct) => {
    if (pct >= 90) return { text: 'Émancipation quasi-certaine', color: 'emerald' };
    if (pct >= 75) return { text: 'Transformation probable',     color: 'emerald' };
    if (pct >= 60) return { text: 'Chemin favorable',            color: 'amber'   };
    if (pct >= 40) return { text: 'Issue incertaine',            color: 'purple'  };
    return           { text: 'Reproduction dominante',          color: 'red'     };
  };

  let proba = 0;
  if (phase === 'finished' && currentSolution?.interpretation) {
    proba = currentSolution.interpretation.realWorldProbability ?? currentSolution.interpretation.theoreticalPromise ?? 10;
  } else {
    proba = engine?.state?.optimalPath?.probabilityOfSuccess ?? engine?.state?.prediction?.probability ?? 0;
  }
  const percentage = Math.max(0, Math.min(100, proba > 1 ? proba : proba * 100));
  const status     = getStatus(percentage);

  const barColor = {
    emerald: '#10b981', amber: '#f59e0b', red: '#ef4444', purple: '#a78bfa',
  }[status.color];

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <div className={`${MONO_LABEL} text-white/25 mb-1`}>Probabilité</div>
          <span className="text-3xl font-bold font-mono text-white tabular-nums">
            {percentage.toFixed(1)}<span className="text-lg text-white/40">%</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <StatusTag color={status.color}>{status.text}</StatusTag>
          <button
            onClick={() => setShowBayesNotice(true)}
            className="opacity-25 hover:opacity-70 transition-opacity"
          >
            <HelpCircle size={11} />
          </button>
        </div>
      </div>

      {/* Barre */}
      <div className="h-1.5 rounded-full overflow-hidden bg-white/5">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{ background: barColor, boxShadow: `0 0 8px ${barColor}60` }}
        />
      </div>

      {/* Source */}
      <div className={`${MONO_LABEL} text-white/15`}>
        {phase === 'finished' ? 'Données historiques réelles' : 'Prédiction optimisation génétique'}
      </div>

      {/* Modal Bayes */}
      {createPortal(
        <AnimatePresence>
          {showBayesNotice && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-black/85 backdrop-blur-sm"
              onClick={() => setShowBayesNotice(false)}
            >
              <motion.div
                initial={{ scale: 0.96, y: 8 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.96, y: 8 }}
                onClick={e => e.stopPropagation()}
                className="max-w-md w-full rounded-2xl p-7 shadow-2xl"
                style={{ background: '#0d0d14', border: '1px solid rgba(167,139,250,0.2)' }}
              >
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-base font-semibold text-violet-300">Calibration bayésienne</h3>
                    <div className={`${MONO_LABEL} text-white/25 mt-0.5`}>Bifurcation Engine</div>
                  </div>
                  <button
                    onClick={() => setShowBayesNotice(false)}
                    className="p-1.5 rounded-lg hover:bg-white/5 text-white/30 hover:text-white transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="space-y-3 text-[12px] leading-relaxed text-white/60">
                  <p>
                    Ce pourcentage n'est pas une prophétie. C'est la{' '}
                    <span className="text-white/90 font-medium">fréquence historique réelle</span>{' '}
                    (1900–2025) de trajectoires similaires.
                  </p>
                  <div
                    className="px-4 py-3 rounded-lg text-[11px] italic text-violet-300/80"
                    style={{ background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.1)' }}
                  >
                    Exemple : Une école émancipatrice réussit dans la simulation à 95 %… mais dans le monde réel, 88 % ont été écrasées → probabilité réelle ≈ 12 %.
                  </div>
                  <p className={`${MONO_LABEL} text-white/20`}>
                    {phase === 'finished' ? 'Cas historiques réels similaires.' : 'Prédiction théorique — optimisation génétique.'}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export const HistoryPanel = () => {
  const { history } = usePesa();

  return (
    <div className="flex flex-col max-h-[260px]">
      <div className="flex-1 overflow-y-auto px-3 py-2.5 space-y-1 scrollbar-thin scrollbar-thumb-white/5 scrollbar-track-transparent">
        {history.slice().reverse().map((h, i) => (
          <div
            key={i}
            className="flex items-start gap-2.5 px-2.5 py-2 rounded-lg hover:bg-white/[0.03] transition-colors group"
          >
            <div
              className="shrink-0 w-0.5 self-stretch rounded-full mt-0.5"
              style={{ background: 'rgba(245,158,11,0.3)' }}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <span className={`${MONO_LABEL} text-amber-500/50`}>
                  {new Date().toLocaleTimeString().slice(3, 8)}
                </span>
                <span className={`${MONO_LABEL} text-white/60 font-semibold`}>{h.type}</span>
              </div>
              <p className={`${MONO_LABEL} text-white/25 italic truncate`}>
                {h.actionId || 'Internal_Shift'}
              </p>
            </div>
          </div>
        ))}
        {history.length === 0 && (
          <div className="text-center py-8">
            <span className={`${MONO_LABEL} text-white/15`}>Aucun événement</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const SolutionsPanel = () => {
  const { currentSolution, phase } = usePesa();
  const isPlaying = phase === 'playing' || phase === 'loading';

  return (
    <div className="p-4 space-y-3">
      {isPlaying ? (
        <div className="space-y-2.5">
          <div className={`${MONO_LABEL} text-white/20`}>Calcul de convergence</div>
          <div className="flex gap-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-0.5 flex-1 rounded-full bg-amber-500/20 animate-pulse"
                style={{ animationDelay: `${i * 100}ms` }}
              />
            ))}
          </div>
        </div>
      ) : currentSolution ? (
        <div className="space-y-3">
          {/* Nom + résolu */}
          <div className="flex items-center gap-2.5">
            <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />
            <span className="text-sm font-semibold text-white leading-tight">{currentSolution.name}</span>
            <StatusTag color="emerald">RÉSOLU</StatusTag>
          </div>

          {/* Description */}
          <p className="text-[11px] leading-relaxed text-white/40 line-clamp-2">
            {currentSolution.description}
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-2">
            <div
              className="px-3 py-2 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
            >
              <div className={`${MONO_LABEL} text-white/25 mb-0.5`}>Complexité</div>
              <div className="text-[11px] font-mono text-white/70">{currentSolution.difficulty}</div>
            </div>
            <div
              className="px-3 py-2 rounded-lg"
              style={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.1)' }}
            >
              <div className={`${MONO_LABEL} text-white/25 mb-0.5`}>Stabilité</div>
              <div className="text-[11px] font-mono text-emerald-400">94%</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg"
          style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.1)' }}
        >
          <Activity size={11} className="animate-pulse text-red-400 shrink-0" />
          <span className={`${MONO_LABEL} text-red-400`}>Équilibre non atteint</span>
        </div>
      )}
    </div>
  );
};

export const CounterfactualsPanel = ({ counterfactuals = [] }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  if (!counterfactuals?.length) return null;

  const top3 = [...counterfactuals]
    .sort((a, b) => b.deltaProbability - a.deltaProbability)
    .slice(0, 3);

  return (
    <div className="p-4 space-y-3">
      {/* Liste top 3 */}
      <div className="space-y-2">
        {top3.map((cf, i) => (
          <div key={i} className="flex items-center gap-3 group">
            {/* Impact bar */}
            <div
              className="h-6 rounded shrink-0 flex items-center px-2"
              style={{
                width: `${Math.max(28, cf.deltaProbability * 100 * 0.6)}px`,
                background: 'rgba(167,139,250,0.1)',
                border: '1px solid rgba(167,139,250,0.15)',
                transition: 'width 0.3s ease',
              }}
            >
              <span className="text-[9px] font-mono font-bold text-violet-400 whitespace-nowrap">
                +{(cf.deltaProbability * 100).toFixed(0)}%
              </span>
            </div>
            <span className="text-[11px] text-white/50 truncate group-hover:text-white/75 transition-colors">
              {cf.actionTitle}
            </span>
          </div>
        ))}
      </div>

      <button
        onClick={() => setIsModalOpen(true)}
        className={`
          w-full py-2 rounded-lg text-[9px] font-mono tracking-widest uppercase
          border border-violet-500/15 text-violet-400/50
          hover:bg-violet-500/8 hover:text-violet-300 hover:border-violet-500/25
          transition-all duration-150
        `}
      >
        Analyse complète →
      </button>

      {/* Modal */}
      {createPortal(
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
              onClick={() => setIsModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.96, y: 12 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.96, y: 12 }}
                onClick={e => e.stopPropagation()}
                className="max-w-2xl w-full max-h-[85vh] overflow-hidden rounded-2xl flex flex-col"
                style={{ background: '#0d0d14', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 0 60px rgba(0,0,0,0.8)' }}
              >
                {/* Header */}
                <div className="px-6 py-5 border-b border-white/5 flex justify-between items-start shrink-0">
                  <div>
                    <h2 className="text-base font-semibold text-white">Analyse des Futurs</h2>
                    <div className={`${MONO_LABEL} text-violet-400/50 mt-0.5`}>Bifurcation Engine v1.0</div>
                  </div>
                  <button onClick={() => setIsModalOpen(false)} className="p-1.5 hover:bg-white/5 rounded-lg transition-colors text-white/30 hover:text-white">
                    <X size={16} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {counterfactuals.map((cf, i) => (
                    <div
                      key={i}
                      className="p-5 rounded-xl transition-colors"
                      style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <StatusTag color="amber">+{(cf.deltaProbability * 100).toFixed(1)}% impact</StatusTag>
                      </div>
                      <h4 className="text-sm font-medium text-white/90 mb-1.5">{cf.actionTitle}</h4>
                      <p className="text-[12px] text-white/55 leading-relaxed">{cf.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

const CausalContent = ({ isModal = false }) => {
  const [activeTab, setActiveTab] = useState('causal');
  const { engine, completedActions } = usePesa();

  const completedArr  = completedActions instanceof Set ? [...completedActions] : [];
  const lastActionId  = completedArr[completedArr.length - 1] || null;

  const lastAction = useMemo(() => {
    if (!lastActionId) return null;
    return engine?.case?.steps?.flatMap(s => s.actions || []).find(a => a.id === lastActionId) || null;
  }, [lastActionId, engine?.case?.steps]);

  const currentStep   = engine?.getCurrentStep?.();
  const globalCausal  = engine?.case?.simulationEngine?.causality || [];
  const stepCausal    = currentStep?.causality || [];
  const causalities   = [...stepCausal, ...globalCausal].slice(0, 6);

  const df   = lastAction?.deepFeedback;
  const cc   = lastAction?.causalChain   || [];
  const comp = lastAction?.competencySignal;
  const hasFeedback = df && (df.whatHappened || df.whyItMatters || df.whatItCosts);

  const tabs = [
    { id: 'causal',     label: 'Causal',     icon: GitBranch  },
    { id: 'feedback',   label: 'Analyse',    icon: Zap,        disabled: !hasFeedback },
    { id: 'competency', label: 'Compétence', icon: Target,     disabled: !comp },
  ];

  return (
    <div className={`${isModal ? '' : ''} space-y-3 p-4`}>
      {/* Context */}
      {lastAction && (
        <div className="flex items-center gap-2">
          <div className="w-1 h-1 rounded-full bg-amber-500/60" />
          <span className={`${MONO_LABEL} text-amber-500/50 truncate`} title={lastAction.label}>
            ↳ {lastAction.id}
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            className={`
              flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg
              text-[9px] font-mono tracking-wider uppercase
              transition-all border
              ${tab.disabled
                ? 'opacity-20 cursor-not-allowed border-white/5 text-white/20'
                : activeTab === tab.id
                ? 'bg-violet-600/15 border-violet-500/25 text-violet-300'
                : 'bg-transparent border-white/5 text-white/30 hover:text-white/55 hover:border-white/10'
              }
            `}
          >
            <tab.icon size={9} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">

        {activeTab === 'causal' && (
          <motion.div key="causal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-1.5">
            {causalities.length > 0 ? causalities.map((c, i) => (
              <div
                key={i}
                className="px-3 py-2.5 rounded-lg transition-colors"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
              >
                <div className="flex items-center gap-2 text-[10px] mb-1.5">
                  <span className="text-white/40 truncate flex-1">{c.cause}</span>
                  <ArrowRight size={8} className="text-white/15 shrink-0" />
                  <span className={`font-medium flex-1 text-right truncate ${(c.weight || 0) > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {c.effect}
                  </span>
                </div>
                <div className="h-0.5 w-full bg-white/5 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${(c.weight || 0) > 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.abs((c.weight || 0) * 100)}%`, opacity: 0.7 }}
                  />
                </div>
                {c.evidence?.source && (
                  <p className={`${MONO_LABEL} text-white/15 mt-1 truncate`}>src: {c.evidence.source}</p>
                )}
              </div>
            )) : (
              <p className={`text-center py-5 ${MONO_LABEL} text-white/15 italic`}>Aucune causalité active</p>
            )}

            {/* Causal chain */}
            {cc.length > 0 && (
              <div className="mt-2 pt-2 border-t border-white/5 space-y-1.5">
                <div className={`${MONO_LABEL} text-white/20 mb-1.5`}>Chaîne causale</div>
                {cc.map((link, i) => (
                  <div key={i} className="flex items-start gap-2 px-2.5 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.015)' }}>
                    <Crosshair size={8} className="text-amber-400/30 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-[10px] text-white/50">{link.from} → {link.to}</p>
                      {link.mechanism && <p className="text-[10px] text-white/25 italic mt-0.5">{link.mechanism}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'feedback' && hasFeedback && (
          <motion.div key="feedback" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2">
            <div className="px-3 py-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.3)' }}>
              <div className={`${MONO_LABEL} text-white/20 mb-0.5`}>Dernière action</div>
              <p className="text-[11px] font-medium text-amber-400/80">{lastAction?.label}</p>
            </div>

            {[
              { key: 'whatHappened', label: "Ce qui s'est passé", icon: Activity,    color: 'text-white/70',    border: 'rgba(255,255,255,0.05)' },
              { key: 'whyItMatters', label: 'Pourquoi ça compte',  icon: BookOpen,   color: 'text-amber-300/80', border: 'rgba(245,158,11,0.1)'   },
              { key: 'whatItCosts',  label: 'Ce que ça coûte',     icon: TrendingDown, color: 'text-red-400/70',  border: 'rgba(239,68,68,0.1)'   },
            ].filter(({ key }) => df?.[key]).map(({ key, label, icon: Icon, color, border }) => (
              <div key={key} className="p-3 rounded-lg space-y-1.5" style={{ border: `1px solid ${border}`, background: 'rgba(255,255,255,0.015)' }}>
                <div className="flex items-center gap-1.5">
                  <Icon size={9} className={color} />
                  <span className={`${MONO_LABEL} text-white/20`}>{label}</span>
                </div>
                <p className={`text-[11px] leading-relaxed ${color}`}>{df[key]}</p>
              </div>
            ))}

            {df?.boostCondition && (
              <div className="p-3 rounded-lg" style={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.12)' }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <TrendingUp size={9} className="text-emerald-400" />
                  <span className={`${MONO_LABEL} text-emerald-400/50`}>Levier suivant</span>
                </div>
                <p className="text-[11px] text-emerald-300/70 italic leading-relaxed">{df.boostCondition}</p>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'competency' && comp && (
          <motion.div key="competency" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2.5">
            <div className={`p-4 rounded-xl border ${LEVEL_COLORS_CAUSAL[comp.level] || LEVEL_COLORS_CAUSAL.intermédiaire}`}>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <p className="text-sm font-semibold text-white/90">{comp.name}</p>
                <StatusTag color={comp.level === 'expert' ? 'purple' : comp.level === 'avancé' ? 'amber' : 'emerald'}>
                  {comp.level}
                </StatusTag>
              </div>
              <p className="text-[11px] text-white/50 leading-relaxed">{comp.descriptor}</p>
            </div>

            {completedArr.length > 1 && (
              <div className="space-y-1">
                <div className={`${MONO_LABEL} text-white/20 mb-1.5`}>Signaux précédents</div>
                {completedArr.slice(0, -1).reverse().slice(0, 3).map(actionId => {
                  const act = engine?.case?.steps?.flatMap(s => s.actions || []).find(a => a.id === actionId);
                  const sig = act?.competencySignal;
                  if (!sig) return null;
                  return (
                    <div key={actionId} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <Shield size={8} className="text-white/15 shrink-0" />
                      <span className="text-[10px] text-white/40 truncate">{sig.name}</span>
                      <span className={`${MONO_LABEL} text-white/20 ml-auto`}>{sig.level}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const CausalPanel = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <CausalContent />

      <div className="px-4 pb-4">
        <button
          onClick={() => setIsModalOpen(true)}
          className={`
            w-full py-2 rounded-lg text-[9px] font-mono tracking-widest uppercase
            border border-white/5 text-white/20
            hover:bg-white/[0.03] hover:text-white/40 hover:border-white/8
            transition-all duration-150
          `}
        >
          Vue étendue →
        </button>
      </div>

      {createPortal(
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
              onClick={() => setIsModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.96, y: 12 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.96, y: 12 }}
                onClick={e => e.stopPropagation()}
                className="max-w-2xl w-full max-h-[85vh] overflow-hidden rounded-2xl flex flex-col"
                style={{ background: '#0d0d14', border: '1px solid rgba(167,139,250,0.15)', boxShadow: '0 0 60px rgba(0,0,0,0.9)' }}
              >
                <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between shrink-0">
                  <div>
                    <h2 className="text-base font-semibold text-white">Graphe Causal</h2>
                    <div className={`${MONO_LABEL} text-violet-400/40 mt-0.5`}>Vue complète</div>
                  </div>
                  <button onClick={() => setIsModalOpen(false)} className="p-1.5 hover:bg-white/5 rounded-lg text-white/30 hover:text-white transition-colors">
                    <X size={16} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <CausalContent isModal />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export const TheoryPanelSide = ({ scenario }) => {
  const { caseId } = useParams();
  const { engine } = usePesa();

  const openLiveTheory = () => {
    const activeId = caseId || scenario?.slug || engine?.case?.id;
    if (activeId) window.open(`/theory/${activeId}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="p-4 space-y-3">
      {/* Concepts clés */}
      {scenario?.theory?.mainConcepts?.slice(0, 3).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {scenario.theory.mainConcepts.slice(0, 3).map((concept, i) => (
            <StatusTag key={i} color="amber"># {concept}</StatusTag>
          ))}
        </div>
      )}

      <button
        onClick={openLiveTheory}
        className={`
          w-full py-2.5 rounded-lg flex items-center justify-center gap-2
          text-[9px] font-mono tracking-widest uppercase
          border border-amber-500/15 text-amber-400/50
          hover:bg-amber-500/8 hover:text-amber-300 hover:border-amber-500/25
          transition-all duration-150
        `}
      >
        <Maximize2 size={11} />
        Mode Conférence
      </button>
    </div>
  );
};

export const RadialParameters = () => {
  const { darkMode } = useTheme();
  const { currentSolution, phase, currentCase, parameters, initialParameters } = usePesa();

  const displayValues = useMemo(() => {
    if (
      Object.keys(parameters).length > 0 &&
      Object.values(parameters).every(v => typeof v === 'number' && !isNaN(v))
    ) {
      return { ...parameters };
    }
    return { ...initialParameters };
  }, [parameters, initialParameters]);

  const { keys, values, labels } = useMemo(() => {
    const formatLabel = (k) =>
      k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, s => s.toUpperCase()).trim();

    const extractedKeys = Object.keys(displayValues).filter(
      k => typeof displayValues[k] === 'number' && !isNaN(displayValues[k])
    );

    return {
      keys: extractedKeys,
      values: extractedKeys.map(k => displayValues[k] ?? 50),
      labels: extractedKeys.map(formatLabel),
    };
  }, [displayValues]);

  const deltaInfo = useMemo(() => {
    if (!currentSolution?.expectedMetrics || phase !== 'finished') return null;
    const expected = currentSolution.expectedMetrics;
    const valid = keys.filter(k => expected[k] !== undefined);
    if (valid.length === 0) return null;

    const avgE = Math.round(valid.reduce((s, k) => s + (expected[k] || 0), 0) / valid.length);
    const avgC = Math.round(valid.reduce((s, k) => s + (displayValues[k] || 50), 0) / valid.length);

    return { delta: avgC - avgE, avgExpected: avgE, avgCurrent: avgC };
  }, [currentSolution, phase, keys, displayValues]);

  const status = !deltaInfo
    ? { label: 'ACQUISITION',    color: '#a78bfa' }
    : deltaInfo.delta >= 10
    ? { label: 'SURPERFORMANCE', color: '#34d399' }
    : deltaInfo.delta < 0
    ? { label: 'ÉROSION',        color: '#f87171' }
    : { label: 'COHÉRENCE',      color: '#a78bfa' };

  const radarData = useMemo(() => ({
    labels,
    datasets: [{
      label: 'Spectre Actuel',
      data: values,
      backgroundColor: `${status.color}08`,
      borderColor: status.color,
      borderWidth: 1.5,
      pointBackgroundColor: status.color,
      pointBorderColor: 'rgba(255,255,255,0.12)',
      pointBorderWidth: 1,
      pointRadius: 3,
      pointHoverRadius: 5,
      tension: 0.3,
    }],
  }), [labels, values, status.color]);

  const radarOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    animation: { duration: 500, easing: 'easeOutQuart' },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(8,8,14,0.97)',
        titleFont: { family: '"DM Mono", monospace', size: 10 },
        bodyFont:  { family: '"DM Mono", monospace', size: 10 },
        padding: 10,
        borderColor: 'rgba(167,139,250,0.15)',
        borderWidth: 1,
        callbacks: { label: (ctx) => ` ${ctx.parsed.r}%` },
      },
    },
    scales: {
      r: {
        angleLines: { color: 'rgba(255,255,255,0.03)' },
        grid:       { color: 'rgba(255,255,255,0.05)', lineWidth: 0.5 },
        pointLabels: {
          color:  'rgba(255,255,255,0.4)',
          font:   { size: 9, family: '"DM Mono", monospace', weight: '400' },
          padding: 8,
        },
        ticks:      { display: false, stepSize: 25 },
        min: 0, max: 100, beginAtZero: true,
      },
    },
  }), [darkMode]);

  if (values.length === 0) {
    return (
      <div
        className="rounded-xl p-6 text-center"
        style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.05)' }}
      >
        <span className="text-[9px] font-mono text-white/20 tracking-widest uppercase">
          Aucune métrique détectée
        </span>
      </div>
    );
  }

  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{
        background: 'rgba(0,0,0,0.25)',
        border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(12px)',
      }}
    >
      {/* Grid overlay — densité identique à LabGrid de CasePage */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.012) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.012) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px',
        }}
      />

      {/* Corner brackets */}
      <div className="absolute top-0 left-0 w-5 h-5 border-t border-l rounded-tl-xl" style={{ borderColor: 'rgba(167,139,250,0.18)' }} />
      <div className="absolute top-0 right-0 w-5 h-5 border-t border-r rounded-tr-xl" style={{ borderColor: 'rgba(167,139,250,0.18)' }} />
      <div className="absolute bottom-0 left-0 w-5 h-5 border-b border-l rounded-bl-xl" style={{ borderColor: 'rgba(167,139,250,0.07)' }} />
      <div className="absolute bottom-0 right-0 w-5 h-5 border-b border-r rounded-br-xl" style={{ borderColor: 'rgba(167,139,250,0.07)' }} />

      <div className="relative z-10 p-4">

        {/* ── Header ───────────────────────────────────────── */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Activity size={10} style={{ color: 'rgba(167,139,250,0.55)' }} />
              <span className="text-[9px] font-mono tracking-[0.22em] uppercase text-white/25">
                Métriques Système
              </span>
            </div>
            <h2 className="text-sm font-semibold text-white/80 tracking-tight">
              Analyse Spectrale
            </h2>
          </div>

          {/* Slug — reprend le style des badges step/phase du HUD overlay */}
          <div
            className="px-2 py-1 rounded-lg text-[9px] font-mono tracking-widest"
            style={{
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,255,255,0.05)',
              color: 'rgba(167,139,250,0.55)',
            }}
          >
            {currentCase?.slug?.toUpperCase() || '—'}
          </div>
        </div>

        {/* ── Radar ────────────────────────────────────────── */}
        <div className="flex justify-center mb-4">
          <div className="w-full max-w-[280px]">
            <Radar data={radarData} options={radarOptions} />
          </div>
        </div>

        {/* ── Status row ───────────────────────────────────── */}
        <div
          className="flex items-center justify-between px-3 py-2.5 rounded-lg mb-3"
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0"
              style={{ background: status.color, boxShadow: `0 0 6px ${status.color}` }}
            />
            <div>
              <div className="text-[8px] font-mono text-white/20 uppercase tracking-widest mb-0.5">
                Statut
              </div>
              <div className="text-[10px] font-mono font-semibold" style={{ color: status.color }}>
                {status.label}
              </div>
            </div>
          </div>

          <div className="text-right">
            <div className="text-[8px] font-mono text-white/20 uppercase tracking-widest mb-0.5">
              Variance
            </div>
            <div
              className="text-lg font-bold font-mono tabular-nums"
              style={{ color: deltaInfo?.delta < 0 ? '#f87171' : '#34d399' }}
            >
              {deltaInfo ? `${deltaInfo.delta > 0 ? '+' : ''}${deltaInfo.delta}%` : '—'}
            </div>
          </div>
        </div>

        {/* ── Metrics grid with evolution indicators ───────────────── */}
        <div className="grid grid-cols-2 gap-2">
          {keys.slice(0, 8).map((k, i) => {
            const val = displayValues[k] ?? 0;
            const pct = Math.min(val, 100);
            
            // Calcul de l'évolution par rapport aux paramètres initiaux
            const initialVal = initialParameters?.[k];
            const delta = initialVal !== undefined ? val - initialVal : null;
            const deltaAbs = delta !== null ? Math.abs(Math.round(delta)) : null;
            const deltaColor = delta === null ? '' : delta > 0 ? '#34d399' : delta < 0 ? '#f87171' : 'rgba(255,255,255,0.4)';
            const deltaSymbol = delta === null ? '' : delta > 0 ? '↑' : delta < 0 ? '↓' : '•';
            
            return (
              <div
                key={k}
                className="relative overflow-hidden rounded-lg"
                style={{
                  background: 'rgba(10,10,18,0.7)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(4px)',
                }}
              >
                {/* Barre de progression plus lumineuse */}
                <div
                  className="absolute inset-y-0 left-0 rounded-l-lg"
                  style={{
                    width: `${pct}%`,
                    background: `linear-gradient(90deg, ${status.color}30, ${status.color}18)`,
                    transition: 'width 0.7s cubic-bezier(0.2, 0.9, 0.4, 1.1)',
                    boxShadow: `0 0 8px ${status.color}20`,
                  }}
                />
                
                <div className="relative flex items-center justify-between gap-2 px-3 py-2.5">
                  <div className="flex items-center gap-1.5 overflow-hidden">
                    <span 
                      className="text-[9px] font-mono font-medium uppercase tracking-wide truncate"
                      style={{ color: 'rgba(255,255,255,0.65)', textShadow: '0 0 4px rgba(0,0,0,0.5)' }}
                    >
                      {labels[i]}
                    </span>
                    {delta !== null && (
                      <span
                        className="text-[8px] font-mono tabular-nums font-medium"
                        style={{ color: deltaColor, textShadow: '0 0 2px rgba(0,0,0,0.3)' }}
                      >
                        {deltaSymbol} {deltaAbs}%
                      </span>
                    )}
                  </div>
                  <span 
                    className="text-[12px] font-mono font-bold tabular-nums"
                    style={{ color: '#ffffffcc', textShadow: '0 0 6px rgba(0,0,0,0.3)' }}
                  >
                    {val}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Solution cible ───────────────────────────────── */}
        {currentSolution && phase === 'finished' && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="mt-3 flex items-center gap-2.5 px-3 py-2.5 rounded-lg"
            style={{
              background: 'rgba(245,158,11,0.05)',
              border: '1px dashed rgba(245,158,11,0.18)',
            }}
          >
            <Target size={11} style={{ color: 'rgba(245,158,11,0.65)' }} className="shrink-0" />
            <div>
              <span className="text-[8px] font-mono text-white/25 uppercase tracking-widest block mb-0.5">
                Cible
              </span>
              <span
                className="text-[10px] font-mono font-semibold uppercase"
                style={{ color: 'rgba(245,158,11,0.8)' }}
              >
                {currentSolution.name}
              </span>
            </div>
          </motion.div>
        )}

      </div>
    </div>
  );
};


const FieldHUD = ({ state, caseData, onClose, isProjection }) => {
  const phase    = state?.currentPhase;
  const stepIdx  = state?.currentStepIndex ?? 0;
  const step     = caseData?.steps?.[stepIdx];
  const tension  = step?.narrativeArc?.tensionLevel ?? 2;

  const tensionColors = ['', '#64748b', '#3b82f6', '#f59e0b', '#f97316', '#ef4444'];
  const tensionLabels = ['', 'STABLE', 'ACTIF', 'TENDU', 'CRITIQUE', 'RUPTURE'];
  const tColor = tensionColors[tension] || '#a78bfa';

  return (
    <>
      {/* ── Barre top : titre + contrôles ─────────────────────────── */}
      <div
        className="absolute top-0 left-0 right-0 z-40 flex items-center justify-between px-8 py-4"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, transparent 100%)',
          backdropFilter: 'blur(0px)',
        }}
      >
        {/* Identité du cas */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span
              className="relative flex h-2 w-2"
              title="Simulation en cours"
            >
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
            </span>
            <span className="text-[9px] font-mono tracking-[0.25em] uppercase text-amber-400/70">
              Champ Vivant — Live
            </span>
          </div>
          <div className="h-3 w-px bg-white/10" />
          <span className="text-[11px] font-semibold text-white/70 truncate max-w-[300px]">
            {caseData?.title || 'Simulation'}
          </span>
        </div>

        {/* Contrôles */}
        <div className="flex items-center gap-2">
          {/* Mode projection : indique que la vue est détachée visuellement */}
          {isProjection && (
            <div
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[9px] font-mono tracking-widest uppercase"
              style={{
                background: 'rgba(99,102,241,0.12)',
                border: '1px solid rgba(99,102,241,0.2)',
                color: 'rgba(165,180,252,0.8)',
              }}
            >
              <Maximize2 size={9} />
              Projection
            </div>
          )}

          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg transition-all"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
          >
            <X size={14} className="text-white/60" />
          </button>
        </div>
      </div>

      {/* ── Phase + Step — coin bas gauche ────────────────────────── */}
      {step && (
        <div
          className="absolute bottom-8 left-8 z-40 max-w-sm pointer-events-none"
          style={{
            background: 'rgba(0,0,0,0.6)',
            border: '1px solid rgba(255,255,255,0.06)',
            backdropFilter: 'blur(12px)',
            borderRadius: '10px',
            padding: '12px 16px',
          }}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[8px] font-mono tracking-widest uppercase text-white/20">
              STEP {String(stepIdx + 1).padStart(2, '0')}
            </span>
            {phase?.type && (
              <>
                <span className="text-white/10">·</span>
                <span className="text-[8px] font-mono tracking-widest uppercase text-white/20">
                  {phase.type}
                </span>
              </>
            )}
          </div>
          <p className="text-sm font-semibold text-white/80 leading-tight">{step.title}</p>
          {phase?.theoreticalHook && (
            <p className="text-[9px] text-indigo-300/50 italic leading-snug mt-1.5">
              {phase.theoreticalHook}
            </p>
          )}
        </div>
      )}

      {/* ── Tension meter — coin bas droit ────────────────────────── */}
      <div
        className="absolute bottom-8 right-8 z-40 pointer-events-none"
        style={{
          background: 'rgba(0,0,0,0.6)',
          border: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(12px)',
          borderRadius: '10px',
          padding: '10px 14px',
        }}
      >
        <div className="text-[8px] font-mono tracking-widest uppercase text-white/20 mb-2">
          Tension
        </div>
        <div className="flex items-end gap-1 mb-1.5">
          {[1, 2, 3, 4, 5].map(i => (
            <div
              key={i}
              className="w-2 rounded-sm transition-all duration-300"
              style={{
                height: `${8 + i * 3}px`,
                background: i <= tension ? tColor : 'rgba(255,255,255,0.06)',
                boxShadow: i <= tension ? `0 0 6px ${tColor}60` : 'none',
              }}
            />
          ))}
        </div>
        <div className="text-[8px] font-mono text-right" style={{ color: `${tColor}80` }}>
          {tensionLabels[tension]}
        </div>
      </div>

      {/* ── Métriques live — top droit, discret ───────────────────── */}
      {state?.parameters && (
        <div
          className="absolute top-16 right-8 z-40 pointer-events-none space-y-1.5"
          style={{ marginTop: '12px' }}
        >
          {Object.keys(state.parameters).slice(0, 5).map(k => {
            const v = state.parameters[k] ?? 0;
            const c = v >= 70 ? '#10b981' : v >= 40 ? '#f59e0b' : '#ef4444';
            return (
              <div key={k} className="flex items-center gap-2.5 justify-end">
                <span className="text-[8px] font-mono text-white/20 uppercase">{k}</span>
                <div
                  className="w-14 h-0.5 rounded-full overflow-hidden"
                  style={{ background: 'rgba(255,255,255,0.06)' }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${v}%`, background: c }}
                  />
                </div>
                <span
                  className="text-[9px] font-mono font-bold tabular-nums"
                  style={{ color: c, minWidth: '1.8rem', textAlign: 'right' }}
                >
                  {v}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Timer si actif ────────────────────────────────────────── */}
      {state?.remainingTime != null && (
        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 pointer-events-none flex items-baseline gap-1.5"
          style={{
            background: 'rgba(0,0,0,0.6)',
            border: '1px solid rgba(245,158,11,0.2)',
            backdropFilter: 'blur(12px)',
            borderRadius: '10px',
            padding: '8px 20px',
          }}
        >
          <span className="text-3xl font-bold font-mono text-amber-400 tabular-nums">
            {String(state.remainingTime).padStart(2, '0')}
          </span>
          <span className="text-base font-semibold" style={{ color: 'rgba(245,158,11,0.5)' }}>S</span>
        </div>
      )}
    </>
  );
};

// ── Modale principale ────────────────────────────────────────────────────────

export const LiveFieldModal = ({ isOpen, onClose }) => {
  const { engine } = usePesa();
  const [isProjection, setIsProjection] = useState(false);

  // Échap pour fermer
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Empêche le scroll du body quand la modale est ouverte
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!engine?.state) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="live-field-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="fixed inset-0 z-[200] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1,    opacity: 1 }}
            exit={{    scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden"
            style={{
              width:  isProjection ? '100vw' : 'calc(100vw - 80px)',
              height: isProjection ? '100vh' : 'calc(100vh - 80px)',
              borderRadius: isProjection ? '0' : '20px',
              border: isProjection ? 'none' : '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 0 120px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.04)',
              background: '#050505',
              transition: 'width 0.35s ease, height 0.35s ease, border-radius 0.35s ease',
            }}
          >
            {/* PESAField live — reçoit le vrai state depuis engine */}
            <PESAField
              externalState={{ ...engine.state, theoryMode: engine.theoryMode }}
              isFullView={true}
            />

            {/* HUD superposé */}
            <FieldHUD
              state={engine.state}
              caseData={engine.case}
              onClose={onClose}
              isProjection={isProjection}
            />

            {/* Bouton projection plein écran — coin top left après le badge */}
            <button
              onClick={() => setIsProjection(v => !v)}
              className="absolute top-4 left-48 z-50 flex items-center justify-center w-8 h-8 rounded-lg transition-all"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
              title={isProjection ? 'Réduire' : 'Mode projection plein écran'}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.15)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
            >
              {isProjection
                ? <Minimize2 size={12} className="text-white/50" />
                : <Shrink    size={12} className="text-white/50" />
              }
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export const PESAField = ({ externalState = null, isFullView = false, onNodeClick }) => {
  const canvasRef     = useRef(null);
  const containerRef  = useRef(null);
  const { darkMode }  = useTheme();
  const { engine: localEngine, theoryMode, getProcessMapPro, currentPhase } = usePesa();

  const [hoveredNode, setHoveredNode]     = useState(null);
  const [hoveredFamily, setHoveredFamily] = useState(null);

  const activeState = useMemo(
    () => externalState || localEngine?.state || {},
    [externalState, localEngine?.state]
  );

  const { families = [], agents = [], interactions = [] } = activeState;

  const visualMode   = currentPhase?.visualMode   || 'network-pulse';
  const ambiance     = currentPhase?.ambiance      || 'default';
  const tensionLevel = localEngine?.case?.steps?.[activeState.currentStepIndex]
                         ?.narrativeArc?.tensionLevel ?? 2;

  const palette    = AMBIANCE_PALETTE[ambiance] || AMBIANCE_PALETTE.default;
  const tension    = TENSION_CONFIG[tensionLevel] || TENSION_CONFIG[2];
  const isFrozen   = visualMode === 'network-freeze';
  const isChaotic  = visualMode === 'network-chaos';

  const liveFamilies = useMemo(
    () => families.filter(f => f.isActive),
    [families]
  );

  const neurons = useMemo(() => {
    const count = liveFamilies.length || 1;
    return liveFamilies.map((f, i) => {
      const angle  = (i / count) * Math.PI * 2 - Math.PI / 2;
      const radius = isFullView ? 0.38 : 0.33;

      const familyAgents = agents.filter(a => a.family === f.name);
      const avgBienEtre  = familyAgents.length
        ? familyAgents.reduce((s, a) => s + (
            a.resources instanceof Map
              ? a.resources.get('bienEtre')
              : (a.resources?.bienEtre ?? 50)
          ), 0) / familyAgents.length
        : 50;

      return {
        id:            f.name,
        name:          f.name,
        narrativeRole: f.narrativeRole || null,
        isToxic:       f.isToxic || false,
        agents:        familyAgents,
        baseX:         Math.cos(angle) * radius,
        baseY:         Math.sin(angle) * radius,
        color:         FAMILY_COLORS[f.name] || '#94a3b8',
        vitality:      avgBienEtre / 100,
      };
    });
  }, [liveFamilies, agents, isFullView]);

  // ── Main render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let time = 0;

    // ── Helpers ──────────────────────────────────────────────────

    /**
     * Crosshair précision + lignes de scan
     */
    const drawCrosshair = (x, y, size, color, alpha) => {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = color;
      ctx.lineWidth   = 0.5;
      // lignes principales
      ctx.beginPath(); ctx.moveTo(x - size, y); ctx.lineTo(x + size, y); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x, y - size); ctx.lineTo(x, y + size); ctx.stroke();
      // petits ticks
      ctx.globalAlpha = alpha * 0.5;
      ctx.lineWidth   = 0.5;
      [0.3, 0.6].forEach(t => {
        ctx.beginPath(); ctx.moveTo(x - size * t, y - 3); ctx.lineTo(x - size * t, y + 3); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x + size * t, y - 3); ctx.lineTo(x + size * t, y + 3); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x - 3, y - size * t); ctx.lineTo(x + 3, y - size * t); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x - 3, y + size * t); ctx.lineTo(x + 3, y + size * t); ctx.stroke();
      });
      ctx.restore();
    };

    const drawTechLabel = (x, y, text, color, size = 9, align = 'center') => {
      ctx.save();
      ctx.font        = `${size}px 'DM Mono', 'Courier New', monospace`;
      ctx.fillStyle   = color;
      ctx.textAlign   = align;
      ctx.shadowBlur  = 6;
      ctx.shadowColor = 'rgba(0,0,0,0.95)';
      ctx.fillText(text, x, y);
      ctx.shadowBlur  = 0;
      ctx.restore();
    };

    const drawScanLine = (w, h, t) => {
      const y    = ((t * 55) % h);
      const grad = ctx.createLinearGradient(0, y - 6, 0, y + 6);
      grad.addColorStop(0,   'transparent');
      grad.addColorStop(0.5, 'rgba(255,255,255,0.012)');
      grad.addColorStop(1,   'transparent');
      ctx.fillStyle = grad;
      ctx.fillRect(0, y - 6, w, 12);
    };

    // ── Render loop ────────────────────────────────────────────

    const render = () => {
      if (!isFrozen) time += isChaotic ? 0.018 : 0.007;

      const dpr = window.devicePixelRatio || 1;
      const w   = canvas.width  / dpr;
      const h   = canvas.height / dpr;
      const cx  = w / 2;
      const cy  = h / 2;

      ctx.clearRect(0, 0, w, h);

      // Background vignette radial
      const bgGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.75);
      bgGrad.addColorStop(0, palette.bg);
      bgGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, w, h);

      // Fine noise scanline
      if (!isFrozen) drawScanLine(w, h, time);

      // Grille fine (subtile)
      ctx.save();
      ctx.globalAlpha = 0.018;
      ctx.strokeStyle = darkMode ? '#fff' : '#000';
      ctx.lineWidth   = 0.4;
      const step = 40;
      for (let x = 0; x < w; x += step) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
      }
      for (let y = 0; y < h; y += step) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
      }
      ctx.restore();

      // Crosshair centre
      drawCrosshair(cx, cy, Math.min(w, h) * 0.07, palette.particle, 0.07);

      // Rings orbitaux dashed
      [0.27, 0.41, 0.54].forEach((r, i) => {
        const radius = Math.min(w, h) * r;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = darkMode
          ? `rgba(255,255,255,${0.018 - i * 0.004})`
          : `rgba(0,0,0,${0.014 - i * 0.003})`;
        ctx.lineWidth   = 0.5;
        ctx.setLineDash([3, 9]);
        ctx.stroke();
        ctx.setLineDash([]);

        // Label radius subtil (coin top)
        if (!isFrozen) {
          const ang = -Math.PI / 2 + time * 0.02 * (i + 1) * 0.3;
          const lx  = cx + Math.cos(ang) * radius;
          const ly  = cy + Math.sin(ang) * radius;
          ctx.save();
          ctx.globalAlpha = 0.12;
          ctx.fillStyle   = palette.particle;
          ctx.beginPath(); ctx.arc(lx, ly, 1.5, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
        }
      });

      // Positions nœuds
      const nodesWithPos = neurons.map(n => {
        const jx = isChaotic ? (Math.sin(time * 0.7 + n.baseX * 10) * tension.chaos * w) : 0;
        const jy = isChaotic ? (Math.cos(time * 0.9 + n.baseY * 10) * tension.chaos * h) : 0;
        return { ...n, x: cx + n.baseX * w + jx, y: cy + n.baseY * h + jy };
      });

      // ── Theory mode: process map edges
      if (theoryMode) {
        const proMap = getProcessMapPro?.() || { edges: [] };
        proMap.edges.forEach(edge => {
          const from = nodesWithPos.find(n => n.id === edge.from);
          const to   = nodesWithPos.find(n => n.id === edge.to);
          if (!from || !to) return;

          ctx.beginPath();
          ctx.moveTo(from.x, from.y);
          const cpx = cx + Math.sin(time * 0.4) * 20;
          const cpy = cy + Math.cos(time * 0.4) * 20;
          ctx.quadraticCurveTo(cpx, cpy, to.x, to.y);
          ctx.strokeStyle = edge.weight < 0
            ? `rgba(239,68,68,${tension.linkOpacity * 1.5})`
            : `rgba(16,185,129,${tension.linkOpacity * 1.5})`;
          ctx.lineWidth = 1 + Math.abs(edge.weight || 0) * 1.5;
          ctx.stroke();

          for (let p = 0; p < 2; p++) {
            const t  = ((time * 0.02 * (1 + Math.abs(edge.weight || 0))) + p * 0.5) % 1;
            const px = from.x + (to.x - from.x) * t;
            const py = from.y + (to.y - from.y) * t;
            ctx.beginPath(); ctx.arc(px, py, 1.5, 0, Math.PI * 2);
            ctx.fillStyle   = edge.weight < 0 ? '#ef4444' : '#10b981';
            ctx.shadowBlur  = 6; ctx.shadowColor = ctx.fillStyle;
            ctx.fill(); ctx.shadowBlur = 0;
          }
        });
      }

      // ── Live interactions
      interactions.forEach(link => {
        const from = nodesWithPos.find(n => n.id === link.source);
        const to   = nodesWithPos.find(n => n.id === link.target);
        if (!from || !to) return;

        const strength = link.strength || 0.5;

        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        const cpx = cx + Math.sin(time * 0.3) * (isChaotic ? 30 : 10);
        const cpy = cy + Math.cos(time * 0.3) * (isChaotic ? 30 : 10);
        ctx.quadraticCurveTo(cpx, cpy, to.x, to.y);
        ctx.strokeStyle = palette.link;
        ctx.lineWidth   = strength * 2;
        ctx.stroke();

        if (!isFrozen && tension.chaos > 0.03) {
          const t  = (time * 0.015) % 1;
          const px = from.x + (to.x - from.x) * t;
          const py = from.y + (to.y - from.y) * t;
          ctx.beginPath(); ctx.arc(px, py, 1, 0, Math.PI * 2);
          ctx.fillStyle   = palette.particle;
          ctx.shadowBlur  = 4; ctx.shadowColor = palette.particle;
          ctx.fill(); ctx.shadowBlur = 0;
        }
      });

      // ── Nodes
      nodesWithPos.forEach(n => {
        const isHovered = n.id === hoveredNode;
        const pulse     = isFrozen ? 0 : Math.sin(time * (isChaotic ? 4 : 2) + n.baseX * 5) * 1.5;
        const jitter    = isChaotic ? (Math.random() - 0.5) * tension.nodeJitter : 0;
        const baseSize  = isFullView ? 20 : 18;
        const size      = baseSize + (1 - n.vitality) * 6 + pulse + jitter;

        // Toxic halo
        if (n.isToxic) {
          ctx.save();
          ctx.globalAlpha = 0.05 + Math.sin(time * 3) * 0.025;
          const toxGrad = ctx.createRadialGradient(n.x, n.y, size, n.x, n.y, size * 3.2);
          toxGrad.addColorStop(0, '#ef4444');
          toxGrad.addColorStop(1, 'transparent');
          ctx.fillStyle = toxGrad;
          ctx.beginPath(); ctx.arc(n.x, n.y, size * 3.2, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
        }

        // Vitality aura
        const auraSize = size * (1.9 + n.vitality * 0.7);
        const auraGrad = ctx.createRadialGradient(n.x, n.y, size * 0.4, n.x, n.y, auraSize);
        auraGrad.addColorStop(0, `${n.color}22`);
        auraGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = auraGrad;
        ctx.beginPath(); ctx.arc(n.x, n.y, auraSize, 0, Math.PI * 2); ctx.fill();

        // Node body
        const nodeGrad = ctx.createRadialGradient(
          n.x - size * 0.3, n.y - size * 0.3, 0,
          n.x, n.y, size
        );
        nodeGrad.addColorStop(0,   `${n.color}FF`);
        nodeGrad.addColorStop(0.6, `${n.color}CC`);
        nodeGrad.addColorStop(1,   `${n.color}88`);

        ctx.beginPath();
        ctx.arc(n.x, n.y, size, 0, Math.PI * 2);
        ctx.fillStyle   = nodeGrad;
        ctx.shadowBlur  = isHovered ? 24 : 10;
        ctx.shadowColor = n.color;
        ctx.fill();
        ctx.shadowBlur  = 0;

        // Border ring
        ctx.strokeStyle = isHovered
          ? 'rgba(255,255,255,0.9)'
          : n.isToxic
          ? 'rgba(239,68,68,0.55)'
          : 'rgba(255,255,255,0.2)';
        ctx.lineWidth   = isHovered ? 1.5 : 0.75;
        ctx.stroke();

        // Inner ring (hover only)
        if (isHovered) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, size * 0.55, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(255,255,255,0.15)';
          ctx.lineWidth   = 0.5;
          ctx.stroke();
          drawCrosshair(n.x, n.y, size * 2.2, n.color, 0.35);
        }

        // Label famille
        const labelY = n.y - size - 12;
        drawTechLabel(n.x, labelY, n.name.toUpperCase(),
          darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', 9);

        // Badge toxic
        if (n.isToxic) {
          drawTechLabel(n.x, labelY - 11, '⚠ DOMINANT', 'rgba(239,68,68,0.65)', 8);
        }

        // Vitality bar
        const barW = size * 2.2;
        const barH = 2;
        const barX = n.x - barW / 2;
        const barY = n.y + size + 7;
        ctx.fillStyle = 'rgba(255,255,255,0.06)';
        ctx.fillRect(barX, barY, barW, barH);
        const vColor = n.vitality > 0.6 ? '#10b981' : n.vitality > 0.35 ? '#f59e0b' : '#ef4444';
        ctx.fillStyle = vColor;
        ctx.fillRect(barX, barY, barW * n.vitality, barH);

        // Agents
        if (isFullView || isHovered) {
          n.agents.slice(0, 3).forEach((agent, idx) => {
            const arch    = agent.archetype || 'neutre';
            const aColor  = ARCHETYPE_ORBIT[arch]?.color || '#94a3b8';
            const nameText = agent.narrativeName || agent.displayName || agent.name;
            ctx.save();
            ctx.font        = '9px "DM Mono", Courier New, monospace';
            ctx.fillStyle   = aColor;
            ctx.globalAlpha = 0.7;
            ctx.textAlign   = 'center';
            ctx.fillText(`· ${nameText}`, n.x, barY + 12 + idx * 11);
            ctx.restore();
          });
        } else {
          n.agents.forEach((agent, idx) => {
            const arch     = agent.archetype || 'neutre';
            const orbitCfg = ARCHETYPE_ORBIT[arch] || { speed: 0.5, radius: 1.0, pulse: false, color: '#94a3b8' };
            const orbitR   = (size + 12) * orbitCfg.radius;
            const angle    = time * orbitCfg.speed + (idx / (n.agents.length || 1)) * Math.PI * 2;
            const ox       = n.x + Math.cos(angle) * orbitR;
            const oy       = n.y + Math.sin(angle) * orbitR;
            const dotSize  = orbitCfg.pulse ? 2 + Math.sin(time * 4 + idx) * 0.8 : 2;

            ctx.beginPath(); ctx.arc(ox, oy, dotSize, 0, Math.PI * 2);
            ctx.fillStyle   = orbitCfg.color;
            ctx.shadowBlur  = orbitCfg.pulse ? 6 : 3;
            ctx.shadowColor = orbitCfg.color;
            ctx.fill(); ctx.shadowBlur = 0;
          });
        }
      });

      // ── HUD bas-gauche — version épurée
      const hudMetrics = [
        { label: 'NODES',  value: liveFamilies.length },
        { label: 'LINKS',  value: interactions.length },
        { label: 'AGENTS', value: agents.length },
        { label: 'TICK',   value: localEngine?.currentTick || 0 },
      ];
      const hudY  = h - 14;
      const hudX0 = 16;
      hudMetrics.forEach((item, i) => {
        const x = hudX0 + i * 76;
        // fond subtil
        ctx.save();
        ctx.globalAlpha = 0.35;
        ctx.fillStyle   = 'rgba(0,0,0,0.5)';
        ctx.fillRect(x - 4, hudY - 22, 68, 24);
        ctx.restore();

        drawTechLabel(x + 28, hudY - 11, item.label, 'rgba(255,255,255,0.22)', 7, 'center');
        drawTechLabel(x + 28, hudY + 1,  String(item.value), palette.particle, 9, 'center');
      });

      // ── Label mode — coin haut droit
      const modeLabel = `${visualMode.toUpperCase()} / ${ambiance.toUpperCase()}`;
      ctx.save();
      ctx.font        = '7px "DM Mono", Courier New, monospace';
      ctx.fillStyle   = 'rgba(255,255,255,0.14)';
      ctx.textAlign   = 'right';
      ctx.fillText(modeLabel, w - 14, 18);
      ctx.restore();

      animId = requestAnimationFrame(render);
    };

    const handleResize = () => {
      const dpr = window.devicePixelRatio || 1;
      const el  = containerRef.current;
      if (!el) return;
      canvas.width  = el.clientWidth  * dpr;
      canvas.height = el.clientHeight * dpr;
      ctx.scale(dpr, dpr);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    render();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, [
    neurons, interactions, theoryMode, darkMode, hoveredNode,
    isFullView, palette, tension, isFrozen, isChaotic,
    localEngine?.currentTick, getProcessMapPro, liveFamilies.length, agents.length,
    visualMode, ambiance,
  ]);

  // ── Mouse events
  const handleMouseMove = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const found = neurons.find(n => {
      const nx = rect.width  / 2 + n.baseX * rect.width;
      const ny = rect.height / 2 + n.baseY * rect.height;
      return Math.hypot(x - nx, y - ny) < 36;
    });
    setHoveredNode(found?.id || null);
    setHoveredFamily(found ? { name: found.name, narrativeRole: found.narrativeRole } : null);
  };

  return (
    <div ref={containerRef} className="w-full h-full relative overflow-hidden">
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => { setHoveredNode(null); setHoveredFamily(null); }}
        onClick={() => hoveredNode && onNodeClick?.(hoveredNode)}
      />

      {/* Tooltip narrativeRole — modernisé */}
      <AnimatePresence>
        {hoveredFamily?.narrativeRole && (
          <motion.div
            key="tooltip"
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0,  scale: 1    }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="absolute bottom-14 left-1/2 -translate-x-1/2 pointer-events-none z-20"
            style={{
              maxWidth: '260px',
              padding: '10px 14px',
              borderRadius: '8px',
              background: 'rgba(6,5,14,0.92)',
              border: '1px solid rgba(167,139,250,0.15)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03)',
            }}
          >
            {/* En-tête famille */}
            <div className="flex items-center gap-1.5 mb-1.5">
              <div
                className="w-1 h-1 rounded-full"
                style={{ background: FAMILY_COLORS[hoveredFamily.name] || '#a78bfa' }}
              />
              <span
                className="text-[9px] font-mono tracking-[0.2em] uppercase"
                style={{ color: FAMILY_COLORS[hoveredFamily.name] || '#a78bfa' }}
              >
                {hoveredFamily.name}
              </span>
            </div>
            <p className="text-[10px] leading-relaxed italic"
               style={{ color: 'rgba(203,213,225,0.75)', margin: 0 }}>
              {hoveredFamily.narrativeRole}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bouton fullscreen — discret, coin haut droit */}
      {!isFullView && (
        <button
          onClick={() => window.open(`/field/${localEngine?.case?.slug}`, '_blank')}
          className="absolute top-3 right-3 transition-all duration-200 group"
          style={{
            padding: '7px',
            borderRadius: '6px',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.06)',
            backdropFilter: 'blur(6px)',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
            e.currentTarget.style.borderColor = 'rgba(167,139,250,0.2)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
          }}
        >
          <Maximize2 size={12} style={{ color: 'rgba(255,255,255,0.4)' }} />
        </button>
      )}
    </div>
  );
};

export const PESAFieldOverlay = ({ analyserRef, isAudioPlaying }) => {
  const { darkMode } = useTheme();
  const {
    engine,
    currentStep,
    currentStepIndex,
    scenario,
    currentPhase,
    applyAction,
    completedActions,
    unlockedActions,
  } = usePesa();

  const [showLearning, setShowLearning]     = useState(false);
  const [lastReaction, setLastReaction]     = useState(null);  // { agentName, text }
  const [revealedDeltas, setRevealedDeltas] = useState(null);  // { key: { before, after, delta } }

  const displayTime    = engine?.state?.remainingTime ?? null;
  const isActionPhase  = currentPhase?.type === 'action';
  const tensionLevel   = currentStep?.narrativeArc?.tensionLevel ?? 2;
  const narrativeArc   = currentStep?.narrativeArc;

  // ── Compute revealDelta when phase has the flag
  useEffect(() => {
    if (!currentPhase?.revealDelta) { setRevealedDeltas(null); return; }
    const init   = engine?.state?.initialParameters || {};
    const params = engine?.state?.parameters        || {};
    const deltas = {};
    Object.keys(params).forEach(k => {
      const before = init[k] ?? 50;
      const after  = params[k] ?? 50;
      deltas[k]    = { before, after, delta: after - before };
    });
    setRevealedDeltas(deltas);
  }, [currentPhase?.revealDelta, engine?.state?.parameters]);
  
  const availableActions = useMemo(() => {
    if (currentPhase?.type !== 'action' || !currentPhase?.actionIds) return [];
    const safeUnlocked  = unlockedActions instanceof Set ? unlockedActions : new Set();
    const safeCompleted = completedActions instanceof Set ? completedActions : new Set();
    return currentPhase.actionIds
      .map(item => {
        const id     = typeof item === 'string' ? item : item.id;
        const action = currentStep?.actions?.find(a => a.id === id);
        if (!action || safeCompleted.has(id) || !safeUnlocked.has(id)) return null;
        return action;
      })
      .filter(Boolean);
  }, [currentPhase, currentStep, unlockedActions, completedActions]);

  // ── Constraint check
  const maxActions = currentPhase?.constraint === 'max-actions-1' ? 1 : null;
  const safeCompleted = completedActions instanceof Set ? completedActions : new Set();
  const actionsThisPhase = (currentPhase?.actionIds || []).filter(id =>
    safeCompleted.has(typeof id === 'string' ? id : id.id)
  ).length;
  const isConstraintReached = maxActions !== null && actionsThisPhase >= maxActions;

  // ── keyAction warning
  const keyActionId   = currentStep?.stepSuccessCriteria?.keyActionId;
  const keyActionDone = keyActionId ? safeCompleted.has(keyActionId) : true;
  const feedbackIfMissed = currentStep?.stepSuccessCriteria?.feedbackIfMissed;

  // ── Action handler with agent reaction toast
  const handleAction = (action) => {
    applyAction(action.id);

    // Find reacting agent
    const agents = engine?.state?.agents || [];
    const reacting = agents.find(a => {
      if (a.reactionsTo instanceof Map) return a.reactionsTo.has(action.id);
      return !!a.reactionsTo?.[action.id];
    });

    if (reacting) {
      const reaction = reacting.reactionsTo instanceof Map
        ? reacting.reactionsTo.get(action.id)
        : reacting.reactionsTo?.[action.id];

      const agentName = reacting.narrativeName || reacting.displayName || reacting.name;
      setLastReaction({ agentName, text: reaction });

      toast.custom(() => (
        <motion.div
          initial={{ opacity: 0, x: 40, scale: 0.95 }}
          animate={{ opacity: 1, x: 0,  scale: 1    }}
          exit={{    opacity: 0, x: 40, scale: 0.95 }}
          className="max-w-sm w-full bg-black/95 border border-amber-500/25 rounded-xl p-4 shadow-2xl backdrop-blur-md"
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-full border border-amber-500/30 bg-amber-500/10 flex items-center justify-center">
              <span className="text-sm font-bold text-amber-400">
                {agentName.charAt(0)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wide truncate">
                  {agentName}
                </p>
                {reacting.archetype && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-white/40 bg-white/5">
                    {reacting.archetype}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-300 leading-relaxed italic">"{reaction}"</p>
            </div>
          </div>
        </motion.div>
      ), { duration: 5500, position: 'bottom-right' });
    }
  };

  // ── Ambiance accent color
  const ambianceAccent = {
    tension:    'text-red-400   border-red-500/30',
    hope:       'text-emerald-400 border-emerald-500/30',
    crisis:     'text-amber-400 border-amber-500/30',
    resolution: 'text-indigo-400 border-indigo-500/30',
    default:    'text-purple-400 border-purple-500/30',
  }[currentPhase?.ambiance || 'default'];

  // ── Tension bar color
  const tensionBarColor = ['', 'bg-slate-500', 'bg-blue-500', 'bg-amber-500', 'bg-orange-500', 'bg-red-500'][tensionLevel] || 'bg-purple-500';

  return (
    <div className="relative w-full h-full flex flex-col pointer-events-none p-6 overflow-y-auto no-scrollbar">

      {/* ════════════════════════════════════════════
          1. HEADER HUD
      ════════════════════════════════════════════ */}
      <div className="flex justify-between items-start z-20 pointer-events-auto shrink-0">

        {/* Left block */}
        <div className="space-y-2">
          {/* Badges de phase */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`flex items-center gap-1.5 px-3 py-1 rounded-md bg-black/60 border ${ambianceAccent} backdrop-blur-md`}>
              <div className={`h-1.5 w-1.5 rounded-full animate-pulse ${
                currentPhase?.ambiance === 'crisis' ? 'bg-amber-400' : 'bg-current'
              }`} />
              <span className="text-[11px] font-mono tracking-widest uppercase">
                {currentPhase?.type || 'INIT'}
              </span>
            </div>

            <div className="px-3 py-1 rounded-md bg-black/40 border border-white/5 backdrop-blur-md">
              <span className="text-[11px] font-mono text-white/50 uppercase tracking-widest">
                STEP {String(currentStepIndex + 1).padStart(2, '0')}/{scenario?.steps?.length || 0}
              </span>
            </div>

            {/* Arc narratif */}
            {narrativeArc?.position && (
              <div className="px-3 py-1 rounded-md bg-black/40 border border-purple-500/15 backdrop-blur-md">
                <span className="text-[11px] font-mono text-purple-400/70 uppercase tracking-widest">
                  {narrativeArc.position}
                </span>
              </div>
            )}

            {/* Constraint */}
            {maxActions !== null && (
              <div className={`px-3 py-1 rounded-md border backdrop-blur-md ${
                isConstraintReached
                  ? 'bg-red-500/20 border-red-500/40 text-red-400'
                  : 'bg-amber-500/10 border-amber-500/30 text-amber-400'
              }`}>
                <span className="text-[11px] font-mono uppercase tracking-widest">
                  {isConstraintReached ? 'LIMITE ATTEINTE' : `MAX ${maxActions} ACTION`}
                </span>
              </div>
            )}
          </div>

          {/* Titre de la phase */}
          {currentPhase?.title && (
            <div className="px-4 py-2 rounded-lg bg-black/50 border border-white/5 backdrop-blur-md max-w-md">
              <p className="text-[11px] font-mono text-white/30 uppercase tracking-widest mb-0.5">
                Phase active
              </p>
              <h3 className="text-base font-semibold text-white leading-tight">
                {currentPhase.title}
              </h3>
            </div>
          )}

          {/* Titre du step */}
          <div className="px-4 py-3 rounded-lg bg-black/70 border border-white/5 backdrop-blur-md max-w-2xl">
            <h2 className="text-2xl font-semibold text-white leading-tight">
              {currentStep?.title || 'ANALYSE EN COURS'}
            </h2>
          </div>
        </div>

        {/* Right : Tension meter */}
        <div className="flex flex-col items-end gap-2">
          <div className="px-4 py-3 rounded-lg bg-black/50 border border-white/5 backdrop-blur-md space-y-2">
            <p className="text-[10px] font-mono uppercase text-white/25 tracking-widest">
              Tension
            </p>
            <div className="flex gap-1 items-center">
              {[1, 2, 3, 4, 5].map(i => (
                <div
                  key={i}
                  className={`h-4 w-2 rounded-sm transition-all ${
                    i <= tensionLevel ? tensionBarColor : 'bg-white/5'
                  }`}
                />
              ))}
            </div>
            <p className="text-[10px] font-mono text-white/30 text-right">
              {['', 'STABLE', 'ACTIF', 'TENDU', 'CRITIQUE', 'RUPTURE'][tensionLevel]}
            </p>
          </div>

          {/* Learning objectives toggle */}
          {currentStep?.learningObjectives?.length > 0 && (
            <button
              onClick={() => setShowLearning(v => !v)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/40 border border-indigo-500/20 text-indigo-400 hover:bg-black/60 transition-all backdrop-blur-md text-[11px] font-mono uppercase tracking-widest"
            >
              <Brain size={11} />
              Objectifs
              {showLearning ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            </button>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════════
          2. LEARNING OBJECTIVES (collapsible)
      ════════════════════════════════════════════ */}
      <AnimatePresence>
        {showLearning && currentStep?.learningObjectives?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{    opacity: 0, height: 0 }}
            className="mt-3 pointer-events-auto"
          >
            <div className="px-5 py-4 rounded-xl bg-black/60 border border-indigo-500/15 backdrop-blur-xl max-w-lg">
              <p className="text-[10px] font-mono uppercase text-indigo-400/60 mb-3 tracking-widest flex items-center gap-2">
                <Brain size={10} /> Ce que cette étape vous apprend
              </p>
              <ul className="space-y-2">
                {currentStep.learningObjectives.map((obj, i) => (
                  <li key={i} className="flex items-start gap-2 text-[10px] text-slate-300 leading-relaxed">
                    <span className="text-indigo-400/60 shrink-0 font-mono">{String(i + 1).padStart(2, '0')}.</span>
                    {obj}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ════════════════════════════════════════════
          3. ZONE CENTRALE — Narration & Actions
      ════════════════════════════════════════════ */}
      <div className="flex-1 flex flex-col justify-center items-center max-w-4xl mx-auto w-full z-10 space-y-4">

        <AnimatePresence mode="wait">

          {/* ── Phase narrative (non-action) */}
          {!isActionPhase && (
            <motion.div
              key="narrative"
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0,  scale: 1    }}
              exit={{    opacity: 0, y: -20, scale: 0.98 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="w-full max-w-2xl pointer-events-auto"
            >
              {/* Narrative card */}
              <div className="p-8 rounded-2xl border border-white/8 backdrop-blur-xl bg-black/70 shadow-2xl text-center relative overflow-hidden">

                <div className="flex items-center justify-center gap-4 mb-5">
                  <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-amber-500/40" />
                  <Activity className="text-amber-400" size={22} />
                  <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-amber-500/40" />
                </div>

                {/* narrativeOverride du step ou description */}
                <p className="text-xl font-light leading-relaxed text-white mb-4 px-4">
                  {currentStep?.narrativeOverride || currentPhase?.narrative || currentStep?.description || 'Le système évolue…'}
                </p>

                {/* Phase narrative spécifique */}
                {currentPhase?.narrative && currentPhase.narrative !== currentStep?.narrativeOverride && (
                  <p className="text-sm text-white/50 leading-relaxed italic mb-4 px-6">
                    {currentPhase.narrative}
                  </p>
                )}

                {/* Voice scanner */}
                <div className="w-full h-12 max-w-md mx-auto relative mb-3">
                  <div className="absolute inset-0 bg-amber-500/4 rounded-lg" />
                  <K2000VoiceScanner isActive analyserRef={analyserRef} darkMode />
                </div>

                <div className="flex justify-center items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse bg-amber-400" />
                  <span className="text-[10px] font-mono opacity-30 uppercase tracking-wider">Signal Stable</span>
                </div>
              </div>

              {/* Theoretical hook */}
              {currentPhase?.theoreticalHook && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-3 px-5 py-4 rounded-xl border border-indigo-500/20 bg-indigo-500/5 backdrop-blur-md"
                >
                  <p className="text-[11px] font-mono text-indigo-400/60 uppercase mb-1.5 flex items-center gap-2 tracking-widest">
                    <BookOpen size={10} /> Ancrage théorique
                  </p>
                  <p className="text-xs text-indigo-200/80 leading-relaxed italic">
                    {currentPhase.theoreticalHook}
                  </p>
                </motion.div>
              )}

              {/* Foreshadow */}
              {narrativeArc?.nextStepForeshadow && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-3 px-5 py-3 rounded-xl border border-white/5 bg-black/30 backdrop-blur-md"
                >
                  <p className="text-[11px] text-white/30 leading-relaxed italic text-center">
                    → {narrativeArc.nextStepForeshadow}
                  </p>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ── Action phase : problematisation + actions */}
          {isActionPhase && (
            <motion.div
              key="action-zone"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{    opacity: 0 }}
              className="w-full max-w-2xl space-y-4 pointer-events-auto"
            >
              {/* Problématisation */}
              {currentStep?.problematisation && (
                <div className="px-7 py-5 rounded-xl bg-black/60 border border-amber-500/20 backdrop-blur-md text-center">
                  <p className="text-lg font-light leading-relaxed italic text-white/90">
                    "{currentStep.problematisation}"
                  </p>
                  {currentStep.epistemologicalQuestion && (
                    <div className="mt-3 text-[10px] font-mono uppercase tracking-wide py-2 px-4 rounded-lg border border-dashed border-purple-400/25 text-purple-300 bg-purple-500/5 inline-block">
                      {currentStep.epistemologicalQuestion}
                    </div>
                  )}
                </div>
              )}

              {/* Hint */}
              {currentPhase?.hint && !isConstraintReached && (
                <div className="px-4 py-3 rounded-lg border border-white/5 bg-black/30 backdrop-blur-md flex items-start gap-2">
                  <Lightbulb size={12} className="text-amber-400/60 mt-0.5 shrink-0" />
                  <p className="text-[10px] text-white/40 leading-relaxed italic">
                    {currentPhase.hint}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2.5">
                {availableActions.map((action, idx) => (
                  <motion.button
                    key={action.id}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: isConstraintReached ? 0.35 : 1, x: 0 }}
                    transition={{ delay: idx * 0.08 }}
                    whileHover={!isConstraintReached ? { x: 5, scale: 1.008 } : {}}
                    onClick={() => !isConstraintReached && handleAction(action)}
                    disabled={isConstraintReached}
                    className={`group relative flex items-center justify-between w-full px-6 py-4 rounded-xl border transition-all duration-200 backdrop-blur-md ${
                      isConstraintReached
                        ? 'bg-black/20 border-white/5 cursor-not-allowed'
                        : 'bg-black/60 border-white/10 hover:border-amber-500/40 hover:bg-black/80 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center gap-4 text-left min-w-0">
                      <span className="text-[10px] font-mono text-white/30 font-semibold shrink-0">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-white truncate">{action.label}</p>
                        {action.competencySignal && (
                          <p className="text-[11px] text-amber-400/60 mt-0.5 font-mono">
                            ↳ {action.competencySignal.name} · {action.competencySignal.level}
                          </p>
                        )}
                      </div>
                    </div>
                    <Zap size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-amber-400 shrink-0 ml-3" />
                  </motion.button>
                ))}

                {availableActions.length === 0 && !isConstraintReached && (
                  <div className="text-center py-6 opacity-30">
                    <p className="text-sm font-mono">Aucune action disponible.</p>
                  </div>
                )}
              </div>

              {/* Continue button */}
              <button
                onClick={() => engine?.advancePhase()}
                className="w-full mt-2 py-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-3 transition-all bg-purple-600 hover:bg-purple-500 text-white shadow-lg border border-purple-500/60"
              >
                CONTINUER → ÉTAPE SUIVANTE
              </button>

              {/* keyAction warning */}
              {!keyActionDone && feedbackIfMissed && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="px-4 py-3 rounded-lg border border-amber-500/25 bg-amber-500/5 flex items-start gap-2"
                >
                  <AlertTriangle size={12} className="text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-[10px] text-amber-300/80 leading-relaxed">
                    {feedbackIfMissed}
                  </p>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── RevealDelta panel */}
        <AnimatePresence>
          {revealedDeltas && (
            <motion.div
              key="reveal-delta"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{    opacity: 0, y: 16 }}
              className="w-full max-w-2xl pointer-events-auto"
            >
              <div className="px-6 py-5 rounded-xl border border-white/8 bg-black/60 backdrop-blur-xl">
                <p className="text-[11px] font-mono uppercase text-white/30 mb-4 tracking-widest flex items-center gap-2">
                  <Activity size={10} /> Delta paramétrique — avant → après
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(revealedDeltas)
                    .filter(([, v]) => v.delta !== 0)
                    .sort((a, b) => Math.abs(b[1].delta) - Math.abs(a[1].delta))
                    .map(([key, { before, after, delta }]) => (
                      <div key={key} className="p-3 rounded-lg border border-white/5 bg-white/2 space-y-1.5">
                        <p className="text-[10px] font-mono uppercase text-white/30 truncate">{key}</p>
                        <div className="flex items-end gap-1.5">
                          <span className="text-lg font-bold text-white font-mono">{after}</span>
                          <span className={`text-[10px] font-bold pb-0.5 ${delta > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {delta > 0 ? '+' : ''}{delta}
                          </span>
                        </div>
                        <div className="h-1 bg-white/8 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${delta > 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
                            style={{ width: `${after}%` }}
                          />
                        </div>
                        <p className="text-[7px] font-mono text-white/20">{before} → {after}</p>
                      </div>
                    ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ════════════════════════════════════════════
          4. FOOTER — Timer + Arc link précédent
      ════════════════════════════════════════════ */}
      <div className="z-20 pointer-events-auto w-full max-w-2xl mx-auto space-y-3 mt-auto shrink-0">

        {/* Previous step link */}
        {narrativeArc?.previousStepLink && (
          <p className="text-center text-[11px] text-white/20 italic font-mono">
            ← {narrativeArc.previousStepLink}
          </p>
        )}

        {/* Timer */}
        {displayTime !== null && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="h-[1px] w-12 bg-gradient-to-r from-transparent to-amber-500/60" />
              <span className="text-[10px] font-mono tracking-widest text-amber-400/70 uppercase">Process Time</span>
              <div className="h-[1px] w-12 bg-gradient-to-l from-transparent to-amber-500/60" />
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-amber-500/8 blur-2xl rounded-full" />
              <div className="relative px-8 py-2.5 rounded-xl border border-amber-500/25 backdrop-blur-md flex items-baseline gap-2 bg-black/50">
                <span className="text-5xl font-bold font-mono text-amber-400">
                  {String(displayTime).padStart(2, '0')}
                </span>
                <span className="text-xl font-semibold text-amber-500/70">S</span>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};


const StartScreen = ({ scenario, engine, darkMode, audioRef, analyserRef, isAudioPlaying, setIsAudioPlaying, initAudioAnalysis }) => (
    <div 
      className="fixed inset-0 bg-gradient-to-br from-slate-950 via-black to-slate-900 z-[1000] flex items-center justify-center overflow-hidden"
      onMouseEnter={initAudioAnalysis} 
      onClick={initAudioAnalysis}
    >
      {/* Background field */}
      <div className="fixed inset-0 z-0 opacity-30">
        <PESAField />
      </div>

      {/* Subtle grid lab overlay */}
      <div 
        className="absolute inset-0 opacity-[0.06]" 
        style={{
          backgroundImage: `
            linear-gradient(#64748b 0.8px, transparent 1px), 
            linear-gradient(90deg, #64748b 0.8px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }} 
      />

      <motion.div 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        className="relative z-10 w-full max-w-5xl p-12 border border-white/5 bg-black/70 backdrop-blur-2xl rounded-3xl shadow-2xl"
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-12 border-b border-white/5 pb-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2.5 h-2.5 rounded-full bg-purple-400 animate-pulse" />
              <h2 className="text-purple-400 text-xs tracking-[3px] uppercase font-medium">SIGNAL DÉTECTÉ • LIVE</h2>
            </div>
            <h1 className="text-6xl font-light text-white tracking-tighter">
              {scenario.title}
            </h1>
          </div>

          <div className="text-right space-y-1">
            <p className="text-xs text-slate-400 uppercase tracking-widest">
              Status: <span className="text-amber-400 font-medium">En attente d'initialisation</span>
            </p>
            <p className="text-xs text-purple-400/70 font-medium">Protocole • PESA</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left column - Visualizer + Goal */}
          <div className="space-y-8">
            <div className="p-8 bg-black/50 border border-white/5 rounded-2xl backdrop-blur-xl">
              <div className="flex items-center justify-between mb-6">
                <p className="text-xs text-purple-300/60 uppercase tracking-widest">Analyse spectrale en temps réel</p>
                <div className={`w-2 h-2 rounded-full ${isAudioPlaying ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'}`} />
              </div>
              
              <SignalVisualizer 
                analyserRef={analyserRef} 
                isActive={isAudioPlaying} 
              />

              <audio 
                ref={audioRef} 
                src="/audio/intro-pesa.mp3" 
                onPlay={() => setIsAudioPlaying(true)} 
                onPause={() => setIsAudioPlaying(false)} 
                onEnded={() => setIsAudioPlaying(false)} 
                autoPlay 
              />
            </div>

            <div className="pl-1">
              <p className="text-slate-300 leading-relaxed italic border-l-2 border-purple-500/30 pl-6 text-[15px]">
                "{scenario.pedagogicalGoal}"
              </p>
            </div>
          </div>

          {/* Right column - Action */}
          <div className="flex flex-col items-center justify-center space-y-8">
            <button 
              onClick={() => {
                initAudioAnalysis();
                if (engine.state.currentPhase?.duration > 0) {
                  engine._schedulePhaseAdvance(engine.state.currentPhase.duration);
                }
                engine.state.isPhaseRunning = true;
                engine._notifyStore();
              }}
              className="group relative w-full py-14 border border-purple-500/30 hover:border-purple-400 bg-black hover:bg-purple-950/30 transition-all duration-700 overflow-hidden rounded-2xl"
            >
              {/* Glow layer */}
              <div className="absolute inset-0 bg-gradient-to-b from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <span className="relative flex items-center justify-center gap-5 text-2xl font-light tracking-tight text-purple-300 group-hover:text-white transition-colors">
                <Zap size={32} className="group-hover:rotate-12 transition-transform duration-500" />
                INITIALISER LE CHAMP VIVANT
              </span>
            </button>

            <p className="text-[11px] text-purple-500/30 uppercase tracking-[2px] text-center">
              Simulation systémique • Irréversible
            </p>
          </div>
        </div>
      </motion.div>
    </div>
); 

const NavButton = ({ active, icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className={`
      w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-[11px] font-mono
      transition-all duration-150 border
      ${active
        ? 'bg-violet-600/15 text-violet-300 border-violet-500/25'
        : 'bg-transparent text-white/35 border-transparent hover:bg-white/[0.04] hover:text-white/70 hover:border-white/[0.06]'
      }
    `}
  >
    <Icon size={13} className={active ? 'text-violet-400' : 'text-white/25'} />
    <span className="flex-1 text-left tracking-wide">{label}</span>
    {active && (
      <motion.div
        layoutId={`nav-active-${label}`}
        className="w-1.5 h-1.5 rounded-full bg-violet-400"
        style={{ boxShadow: '0 0 6px #a78bfa' }}
      />
    )}
  </button>
);

const SpecialButton = ({ label, icon: Icon, color, active, onClick }) => {
  const styles = {
    purple: {
      base:    'border-violet-500/15 text-violet-400/60 bg-transparent',
      hover:   'hover:bg-violet-500/10 hover:text-violet-300 hover:border-violet-500/25',
      activeC: 'bg-violet-600/20 text-violet-300 border-violet-500/30',
    },
    amber: {
      base:    'border-amber-500/15 text-amber-400/60 bg-transparent',
      hover:   'hover:bg-amber-500/10 hover:text-amber-300 hover:border-amber-500/25',
      activeC: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    },
  };
  const s = styles[color] || styles.purple;

  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border
        text-[11px] font-mono tracking-wide
        transition-all duration-150
        ${active ? s.activeC : `${s.base} ${s.hover}`}
      `}
    >
      <Icon size={13} className={active ? '' : 'opacity-60'} />
      <span className="flex-1 text-left">{label}</span>
      {active && <div className="w-1 h-1 rounded-full bg-current opacity-70" />}
    </button>
  );
};

const HeaderTool = ({ icon: Icon, onClick, active }) => (
  <button 
    onClick={onClick} 
    className={`p-3 rounded-lg transition-all backdrop-blur-sm ${
      active 
        ? 'bg-white/10 text-amber-400 border border-white/10' 
        : 'text-slate-400 hover:bg-white/5 border border-transparent hover:border-white/5'
    }`}
  >
    <Icon size={18} />
  </button>
);

const LoadingScreen = ({ darkMode }) => (
  <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-gradient-to-br from-black via-slate-950 to-black">
    <div className="flex flex-col items-center gap-8">
      <motion.div 
        animate={{ rotate: 360 }} 
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }} 
        className="w-20 h-20 border-4 border-purple-600 border-t-transparent rounded-full" 
      />
      <span className="font-mono text-[10px] tracking-wide uppercase opacity-50">Synchronisation moteur PESA</span>
    </div>
  </div>
);


// PAGE PRINCIPALE

export default function CasePage() {
  const {
    engine, loadSimulation, restartSimulation,
    toggleFloatingPanel, toggleTheoryMode,
    phase, scenario, currentStep, 
    openPanels, theoryMode, closeDebrief,
    showDebrief, currentSolution, closeSolution, toggleShowDebrief,
  } = usePesa();

  const { caseId } = useParams();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [leftCollapsed,  setLeftCollapsed]  = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [fieldModalOpen, setFieldModalOpen] = useState(false);

  const initAudioAnalysis = async () => {
    if (audioContextRef.current) {
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      return;
    }

    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;

    try {
      const source = ctx.createMediaElementSource(audioRef.current);
      source.connect(analyser);
      analyser.connect(ctx.destination);

      audioContextRef.current = ctx;
      analyserRef.current = analyser;
      sourceRef.current = source;
    } catch (e) {
      console.error("AudioSource error:", e);
    }
  };

  useEffect(() => {
  const fetchCase = async () => {
    try {
      const res = await api.get(`/cases/${caseId}`); 
      loadSimulation(res.data);
      setLoading(false);
    } catch (err) {
      toast.error("Échec de l'initialisation");
      navigate('/dashboard');
    }
  };
  if (caseId && !engine?.case) fetchCase();
  else if (engine?.case) setLoading(false);
}, [caseId, engine?.case, loadSimulation, navigate]);

  useEffect(() => {
    if (engine?.state && caseId) {
      const syncData = {
        state: engine.state,
        case: engine.case,
        lastUpdate: Date.now()
      };
      localStorage.setItem(`pesa_sync_${caseId}`, JSON.stringify(syncData));
    }
  }, [engine?.state, engine?.case, caseId]);

  const progress = useMemo(() => {
    if (!scenario?.steps?.length) return 0;
    const total = scenario.steps.length;
    const current = engine?.state?.currentStepIndex || 0;
    return Math.min(100, Math.round((current / (total - 1)) * 100));
  }, [scenario, engine?.state?.currentStepIndex]);

  

  if (loading || !scenario) return <LoadingScreen darkMode={darkMode} />;

  if (window.innerWidth < 1024) {  // ou utilise useMediaQuery si tu as une lib
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0a0a0a] text-white text-center">
      <div className="max-w-md">
        <div className="text-6xl mb-6">💻</div>
        <h2 className="text-2xl font-bold mb-4">Simulation optimisée pour ordinateur</h2>
        <p className="opacity-70 mb-8">
          Cette expérience pédagogique est conçue pour être utilisée sur un écran d’ordinateur ou tablette large.<br/>
          Pour la meilleure expérience, ouvrez-la sur un écran ≥ 1024px.
        </p>
        <button 
          onClick={() => window.history.back()}
          className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl border border-white/20"
        >
          Retour
        </button>
      </div>
    </div>
  );
}

  return (
    <div className={`min-h-screen font-sans selection:bg-amber-500/30 transition-colors duration-1000 ${
      darkMode ? 'bg-[#0a0a0a] text-white' : 'bg-slate-50 text-slate-900'
    }`}>
      
      <LabGrid darkMode={darkMode} />
      <Scanlines />
      
      <div className="fixed inset-0 z-0 opacity-30">
        <PESAField />
      </div>

      <div className="relative z-10 flex flex-col h-screen">

        {engine && !engine.state.isPhaseRunning && (
          <StartScreen 
            scenario={scenario}
            engine={engine}
            darkMode={darkMode}
            audioRef={audioRef}
            analyserRef={analyserRef}
            isAudioPlaying={isAudioPlaying}
            setIsAudioPlaying={setIsAudioPlaying}
            initAudioAnalysis={initAudioAnalysis}
          />
        )}
        
        {/* HEADER */}
        <header className={`shrink-0 sticky top-0 z-[60] backdrop-blur-xl border-b transition-all duration-300 ${
          darkMode ? 'bg-black/80 border-white/10' : 'bg-white/80 border-slate-200'
        }`}>
          <div className="max-w-[1920px] mx-auto px-6 h-20 flex items-center">
            
            {/* SECTION GAUCHE : NAVIGATION & INFOS */}
            <div className="flex items-center gap-5 flex-1">
              <button 
                onClick={() => navigate('/dashboard')}
                className={`p-2.5 rounded-xl border transition-all group ${
                  darkMode 
                  ? 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20' 
                  : 'bg-slate-100 border-slate-200 hover:bg-slate-200'
                }`}
                title="Retour au dashboard"
              >
                <ChevronLeft size={20} className="group-hover:-translate-x-0.5 transition-transform" />
              </button>

              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-amber-500/10 text-amber-500 border border-amber-500/20 uppercase tracking-widest">
                    Simulation
                  </span>
                  <h1 className="text-lg font-bold truncate tracking-tight">{scenario.title}</h1>
                </div>
                <p className="text-[11px] opacity-50 font-medium truncate max-w-[300px] mt-0.5">
                  {scenario.pedagogicalGoal}
                </p>
              </div>
            </div>

            {/* SECTION CENTRALE : INDICATEURS DE PERFORMANCE */}
            <div className="flex flex-col items-center gap-2 flex-1 hidden md:flex">
              <div className="flex items-center gap-3">
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] font-bold tracking-tighter uppercase transition-colors ${
                  phase === 'playing' 
                    ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' 
                    : 'bg-amber-500/10 border-amber-500/20 text-amber-500'
                }`}>
                  <span className="relative flex h-2 w-2">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${phase === 'playing' ? 'bg-emerald-400' : 'bg-amber-400'}`}></span>
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${phase === 'playing' ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
                  </span>
                  {phase}
                </div>
                
                <div className="h-4 w-px bg-white/10" />

                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-bold opacity-40 uppercase tracking-widest">Progression</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-1.5 rounded-full overflow-hidden bg-white/5 border border-white/5">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500" 
                        initial={{ width: 0 }} 
                        animate={{ width: `${progress}%` }} 
                        transition={{ duration: 0.8, ease: "circOut" }}
                      />
                    </div>
                    <span className="text-xs font-mono font-bold w-8">{progress}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION DROITE : OUTILS ET ACTIONS */}
            <div className="flex items-center justify-end gap-3 flex-1">
              <div className="flex items-center gap-1.5 p-1 rounded-xl bg-black/20 border border-white/5">
                <HeaderTool 
                  icon={soundEnabled ? Volume2 : VolumeX} 
                  onClick={() => setSoundEnabled(!soundEnabled)} 
                  className="hover:bg-white/5"
                />
                <HeaderTool 
                  icon={darkMode ? Sun : Moon} 
                  
                  active={darkMode}
                  className="hover:bg-white/5"
                />
              </div>

              <button 
                onClick={restartSimulation}
                className="group flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white transition-all text-[11px] font-bold border border-red-500/20"
              >
                <History size={14} className="group-hover:rotate-[-45deg] transition-transform" />
                REINITIALISER
              </button>
              
            </div>

          </div>
        </header>

        {/* GRID PRINCIPAL */}        
        <main className="flex-1 grid gap-0 overflow-hidden relative"
          style={{
            gridTemplateColumns: [
              leftCollapsed  ? '0px' : 'minmax(0,1fr)',
              'minmax(0,2fr)',
              rightCollapsed ? '0px' : 'minmax(0,1fr)',
            ].join(' '),
            // xl : 3-6-3 quand tout ouvert → s'adapte automatiquement
            transition: 'grid-template-columns 350ms cubic-bezier(0.4,0,0.2,1)',
          }}
        >

          {/* ═══════════════════════════════════════
              COLONNE GAUCHE — Panneaux systémiques
          ═══════════════════════════════════════ */}
          <div
            className="flex flex-col gap-6 h-full overflow-hidden"
            style={{
              opacity:    leftCollapsed ? 0 : 1,
              pointerEvents: leftCollapsed ? 'none' : 'auto',
              transition: 'opacity 200ms ease',
              padding:    leftCollapsed ? 0 : '2rem',
              paddingRight: leftCollapsed ? 0 : '1rem',
            }}
          >
            <div className="shrink-0 space-y-4">
              <h3 className="text-[10px] font-semibold opacity-30 uppercase tracking-wide px-2">Systémique</h3>
              <div className="grid grid-cols-1 gap-2">
                <NavButton active={openPanels.dynamics}      icon={Activity}  label="Dynamiques"   onClick={() => toggleFloatingPanel('dynamics')} />
                <NavButton active={openPanels.causal}        icon={Network}   label="Graphe Causal" onClick={() => toggleFloatingPanel('causal')} />
                <NavButton active={openPanels.history}       icon={History}   label="Journal"       onClick={() => toggleFloatingPanel('history')} />
                <NavButton active={openPanels.prediction}    icon={Lightbulb} label="Prédictions"  onClick={() => toggleFloatingPanel('prediction')} />
                <NavButton active={openPanels.theory}        icon={Brain}     label="Théories"     onClick={() => toggleFloatingPanel('theory')} />
                <NavButton active={openPanels.counterfactuals} icon={Factory} label="Contrefactuels" onClick={() => toggleFloatingPanel('counterfactuals')} />
              </div>

              <div className="pt-4 space-y-3">
                <h3 className="text-[10px] font-semibold opacity-30 uppercase tracking-wide px-2">Outils Avancés</h3>
                <div className="flex flex-col gap-3">
                  <SpecialButton
                    label="Champ Vivant"
                    icon={Zap}
                    color="purple"
                    onClick={() => window.open(`/field/${scenario?.slug || 'default'}`, '_blank')}
                  />
                  <SpecialButton
                    label="Modale Champ Vivant"
                    icon={Zap}
                    color="indigo"
                    onClick={() => setFieldModalOpen(true)}   // ← modale au lieu du nouvel onglet
                  />
                  <SpecialButton
                    label={`Théorie: ${theoryMode ? 'ON' : 'OFF'}`}
                    icon={School}
                    color="amber"
                    active={theoryMode}
                    onClick={toggleTheoryMode}
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/5 scrollbar-track-transparent space-y-4 pr-2">
              <AnimatePresence mode="popLayout">
                {openPanels.prediction && (
                  <SoberWrapper key="pred"><PredictionPanel /></SoberWrapper>
                )}
                {openPanels.theory && (
                  <SoberWrapper key="theo"><TheoryPanelSide scenario={engine?.case} /></SoberWrapper>
                )}
                {openPanels.dynamics && (
                  <SoberWrapper key="dyna"><DynamicsPanel /></SoberWrapper>
                )}
                {openPanels.causal && (
                  <SoberWrapper key="caus"><CausalPanel /></SoberWrapper>
                )}
                {openPanels.counterfactuals && phase === 'playing' && (
                  <SoberWrapper key="count"><CounterfactualsPanel counterfactuals={engine?.counterfactuals || []} /></SoberWrapper>
                )}
                {openPanels.history && (
                  <SoberWrapper key="hist"><HistoryPanel /></SoberWrapper>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* ═══════════════════════════════════════
              COLONNE CENTRALE — PESAField
          ═══════════════════════════════════════ */}
          <div className="flex flex-col gap-6 overflow-hidden relative p-8 px-4">

            {/* Toggle LEFT — ancré au bord gauche du centre */}
            <button
              onClick={() => setLeftCollapsed(v => !v)}
              title={leftCollapsed ? 'Afficher le panneau gauche' : 'Masquer le panneau gauche'}
              className={`
                absolute left-0 top-1/2 -translate-y-1/2 z-30
                flex items-center justify-center
                w-5 h-14 rounded-r-lg border-y border-r
                transition-all duration-200
                ${leftCollapsed
                  ? 'bg-purple-600/80 border-purple-500/60 text-white hover:bg-purple-500'
                  : 'bg-black/40 border-white/8 text-white/30 hover:text-white hover:bg-black/60'
                }
                backdrop-blur-md
              `}
            >
              {leftCollapsed
                ? <PanelLeftOpen  size={12} />
                : <PanelLeftClose size={12} />
              }
            </button>

            {/* Toggle RIGHT — ancré au bord droit du centre */}
            <button
              onClick={() => setRightCollapsed(v => !v)}
              title={rightCollapsed ? 'Afficher le panneau droit' : 'Masquer le panneau droit'}
              className={`
                absolute right-0 top-1/2 -translate-y-1/2 z-30
                flex items-center justify-center
                w-5 h-14 rounded-l-lg border-y border-l
                transition-all duration-200
                ${rightCollapsed
                  ? 'bg-purple-600/80 border-purple-500/60 text-white hover:bg-purple-500'
                  : 'bg-black/40 border-white/8 text-white/30 hover:text-white hover:bg-black/60'
                }
                backdrop-blur-md
              `}
            >
              {rightCollapsed
                ? <PanelRightOpen  size={12} />
                : <PanelRightClose size={12} />
              }
            </button>

            {/* PESAFieldOverlay */}
            <div className="relative flex-1 rounded-[3rem] overflow-hidden border shadow-2xl transition-all duration-500 bg-black/10 border-white/5 backdrop-blur-sm mx-5">
              <PESAFieldOverlay
                analyserRef={analyserRef}
                isAudioPlaying={isAudioPlaying}
              />
            </div>
          </div>

          {/* ═══════════════════════════════════════
              COLONNE DROITE — Radar + Solutions
          ═══════════════════════════════════════ */}
          <div
            className="flex flex-col gap-6 overflow-hidden"
            style={{
              opacity:    rightCollapsed ? 0 : 1,
              pointerEvents: rightCollapsed ? 'none' : 'auto',
              transition: 'opacity 200ms ease',
              padding:    rightCollapsed ? 0 : '2rem',
              paddingLeft: rightCollapsed ? 0 : '1rem',
            }}
          >
            <div className="shrink-0">
              <RadialParameters />
            </div>
            <div className="shrink-0">
              <SolutionsPanel onClose={closeSolution} />
            </div>
          </div>

          <LiveFieldModal
            isOpen={fieldModalOpen}
            onClose={() => setFieldModalOpen(false)}
            />

        </main>

        {phase === 'finished' && (
          <motion.button
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => toggleShowDebrief(true)}
            className="fixed bottom-12 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-full font-semibold flex items-center gap-4 bg-purple-600 text-white shadow-lg border border-purple-500"
          >
            <FileText size={20} className="animate-pulse" />
            CONSULTER LE RAPPORT
          </motion.button>
        )}

        <Toaster position="bottom-right" />
        {showDebrief && <RapportEnquete onClose={closeDebrief}/>}
        
      </div>
    </div>
  );
} 